import React from 'react';
import { Calculator, Ruler, Pipeline, Thermometer } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  setErrors,
  errors,
  isCalculating,
  standardDiameters
}) => {
  // Расчет текущего диаметра трубы
  const pipeInnerDiameter = parseFloat(formData.pipeInnerDiameter) || 0;
  
  // Расчет текущей длины трубы
  const pipeLength = parseFloat(formData.pipeLength) || 0;
  
  // Обработчик изменения диаметра трубы
  const handleDiameterChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      pipeInnerDiameter: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.pipeInnerDiameter) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.pipeInnerDiameter;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения длины трубы
  const handleLengthChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      pipeLength: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.pipeLength) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.pipeLength;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения температуры воды
  const handleTemperatureChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      waterTemperature: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.waterTemperature) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.waterTemperature;
        return newErrors;
      });
    }
  };
  
  // Получаем стандартные диаметры для выбранного типа трубы
  const availableDiameters = standardDiameters[formData.pipeType] || [];
  
  // Предопределенные длины труб для быстрого выбора
  const predefinedLengths = [
    { value: '10', label: '10 м' },
    { value: '50', label: '50 м' },
    { value: '100', label: '100 м' },
    { value: '500', label: '500 м' }
  ];
  
  // Предопределенные температуры для быстрого выбора
  const predefinedTemperatures = [
    { value: '5', label: '5°C' },
    { value: '15', label: '15°C' },
    { value: '40', label: '40°C' },
    { value: '60', label: '60°C' }
  ];
  
  // Функция расчета
  const calculateResults = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Диаметр трубы */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="diameterSlider" className="text-gray-600 dark:text-gray-400">Диаметр трубы</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(pipeInnerDiameter, 1)} мм</span>
          </div>
          <input
            type="range"
            id="diameterSlider"
            min="5"
            max="500"
            step="1"
            value={pipeInnerDiameter}
            onChange={handleDiameterChange}
            aria-label="Диаметр трубы"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            disabled={formData.calculationType === 'diameter'}
          />
          
          {/* Кнопки с предопределенными диаметрами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {availableDiameters.slice(0, 4).map(diameter => (
              <button
                key={diameter}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, pipeInnerDiameter: diameter.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.pipeInnerDiameter === diameter.toString() 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                disabled={formData.calculationType === 'diameter'}
              >
                {diameter} мм
              </button>
            ))}
          </div>
        </div>
        
        {/* Длина трубы */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="lengthSlider" className="text-gray-600 dark:text-gray-400">Длина трубы</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(pipeLength, 1)} м</span>
          </div>
          <input
            type="range"
            id="lengthSlider"
            min="1"
            max="1000"
            step="1"
            value={pipeLength}
            onChange={handleLengthChange}
            aria-label="Длина трубы"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными длинами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedLengths.map(length => (
              <button
                key={length.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, pipeLength: length.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.pipeLength === length.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {length.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Температура воды */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="temperatureSlider" className="text-gray-600 dark:text-gray-400">Температура воды</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.waterTemperature}°C</span>
          </div>
          <input
            type="range"
            id="temperatureSlider"
            min="1"
            max="95"
            step="1"
            value={formData.waterTemperature}
            onChange={handleTemperatureChange}
            aria-label="Температура воды"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Предопределенные температуры */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTemperatures.map(temp => (
              <button
                key={temp.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, waterTemperature: temp.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.waterTemperature === temp.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {temp.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа трубы */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Материал трубы</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pipeType: 'steel', pipeRoughness: '0.15' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pipeType === 'steel' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Стальная</div>
              <div className="text-xs opacity-75">Шероховатость 0.15 мм</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pipeType: 'pvc', pipeRoughness: '0.007' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pipeType === 'pvc' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">ПВХ</div>
              <div className="text-xs opacity-75">Шероховатость 0.007 мм</div>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pipeType: 'copper', pipeRoughness: '0.0015' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pipeType === 'copper' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Медная</div>
              <div className="text-xs opacity-75">Шероховатость 0.0015 мм</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pipeType: 'pp', pipeRoughness: '0.004' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pipeType === 'pp' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Полипропилен</div>
              <div className="text-xs opacity-75">Шероховатость 0.004 мм</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateResults}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;